/**
 * useOfflineSync — composite hook that combines online/offline status,
 * sync queue state, and provides a unified API for offline-first operations.
 *
 * Use this in any component that needs to be offline-aware.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNetworkStatus } from './useNetworkStatus.js';
import { useSyncQueue } from './useSyncQueue.js';
import { getSyncQueueCount, getOfflinePhotoCount, cleanupStaleCache } from './useIndexedDB.js';

export interface OfflineSyncState {
  /** Current network status */
  isOnline: boolean;
  /** Whether the app was offline at some point in this session */
  wasOffline: boolean;
  /** Connection quality (4g, 3g, 2g, slow-2g) */
  effectiveType: string | null;
  /** Number of items waiting to sync */
  pendingCount: number;
  /** Number of photos stored offline */
  offlinePhotoCount: number;
  /** Whether sync is currently in progress */
  isSyncing: boolean;
  /** Timestamp of last successful sync */
  lastSyncAt: number | null;
  /** Last sync error message */
  lastError: string | null;
  /** Manually trigger sync */
  triggerSync: () => void;
  /** Enqueue an offline mutation */
  enqueue: ReturnType<typeof useSyncQueue>['enqueue'];
}

export function useOfflineSync(): OfflineSyncState {
  const { isOnline, wasOffline, effectiveType } = useNetworkStatus();
  const { pendingCount, isSyncing, lastSyncAt, lastError, enqueue, processQueue } = useSyncQueue();
  const [offlinePhotoCount, setOfflinePhotoCount] = useState(0);

  // Poll offline photo count
  useEffect(() => {
    const update = () => getOfflinePhotoCount().then(setOfflinePhotoCount).catch(() => {});
    update();
    const interval = setInterval(update, 5000);
    return () => clearInterval(interval);
  }, []);

  // Periodic stale cache cleanup (every 6 hours, removes data older than 7 days)
  useEffect(() => {
    const cleanup = () => cleanupStaleCache().catch(() => {});
    cleanup();
    const interval = setInterval(cleanup, 6 * 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const triggerSync = useCallback(() => {
    if (navigator.onLine) {
      processQueue();
    }
  }, [processQueue]);

  return {
    isOnline,
    wasOffline,
    effectiveType,
    pendingCount,
    offlinePhotoCount,
    isSyncing,
    lastSyncAt,
    lastError,
    triggerSync,
    enqueue,
  };
}
